import LogRocket from "logrocket";
import { PropsWithChildren, useMemo } from "react";
import { Workbox, WorkboxEventMap } from "workbox-window";

import LogRocketEvents from "../../observability/LogRocketEvents";
import config from "../../app.config.json";

import { ServiceWorkerContext } from "./ServiceWorkerContext";

const ServiceWorkerProvider = ({ children }: PropsWithChildren<unknown>) => {
  const context = useMemo(() => {
    const publicPath = config.public_path.endsWith("/")
      ? config.public_path.slice(0, config.public_path.length - 1)
      : config.public_path;

    const wb = new Workbox(`${publicPath}/sw.js`);

    const promisifyWorkBoxEvent = (event: keyof WorkboxEventMap) =>
      new Promise<void>((resolve) => {
        wb.addEventListener(event, () => {
          resolve();
        });
      });

    const installing = promisifyWorkBoxEvent("installing");
    const installed = promisifyWorkBoxEvent("installed");

    const controlling = new Promise<void>((resolve, reject) => {
      wb.controlling
        .then(() => {
          resolve();
        })
        .catch(reject);
    });

    const waitForInstalled = async (timeout = 1000) =>
      new Promise<void>((resolve) => {
        const id = setTimeout(() => {
          resolve();
          LogRocket.track(LogRocketEvents.TimeoutServiceWorkerInstalled);
        }, timeout);

        installed.then(() => {
          clearTimeout(id);
          resolve();
        });
      });

    const register = async (timeout = 500) => {
      if (!("serviceWorker" in navigator)) {
        LogRocket.track(LogRocketEvents.ServiceWorkerNotSupported);
        return;
      }

      const wait = new Promise<void>((resolve) => {
        const id = setTimeout(() => {
          resolve();
          LogRocket.track(LogRocketEvents.TimeoutServiceWorkerRegister);
        }, timeout);

        installing.then(() => {
          clearTimeout(id);
          resolve();
        });

        controlling.then(() => {
          clearTimeout(id);
          resolve();
        });
      });

      await wb.register();
      await wait;
    };

    return {
      register,
      waitForInstalled,
    };
  }, []);

  return (
    <ServiceWorkerContext.Provider value={context}>
      {children}
    </ServiceWorkerContext.Provider>
  );
};

export default ServiceWorkerProvider;
